import produce from 'immer';
import { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { Link } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import Item from 'src/careflow/common/Item';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useDialogStore } from 'src/stores/dialog';
import RequestReport from './report/RequestReport';
import { getPrintableTask } from './helpers/report';
import DetailsSummary from 'src/careflow/common/DetailsSummary';
import RequestReportResponse from './report/RequestReportResponse';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { HJAssessment, mapRequester, memberDetails } from './helpers/task';
import { useBinarys, useDocumentReferences } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { TaskWrapper, WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { WrappedPlanDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PlanDefinition';
import {
  Binary,
  DocumentReference,
  MeasureReport,
  Questionnaire,
  ServiceRequest,
} from 'src/careflow/ph-core/fhir-client/resources';
import { QuestionnaireResponse } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/QuestionnaireResponse';
import {
  getMeasureReportGroups,
  getMeasureReportGroupValue,
  getObservationtypeValue,
} from './helpers/fhir';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { TaskStatusValue } from 'src/@types/crs/task';
import { QuestionnaireAnswerMap } from 'src/careflow/inck/nat-task/report/report-type';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import agent from 'src/api/agent';
import { CaseIntent, CaseStatus } from 'src/crs/case/dto/case.dto';
import useTable from 'src/hooks/useTable';
import { PaginateQuery } from 'src/api/pagination/dtos';
import { CaseResponse, CaseService } from 'src/crs/case/service/CaseService';
import { TaskService } from 'src/careflow/inck/nat-task/service/TaskService';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';

interface Props {
  patient: WrappedPatient | undefined;
  patientDetailsFromReport: any;
  task: WrappedTask | undefined;
  serviceRequest: WrappedServiceRequest | null;
  planDefinition: WrappedPlanDefinition | null;
  questionnaire: Questionnaire[] | undefined;
  questionnaireResponse: QuestionnaireResponse[] | undefined;
  questionnaireAnswerMap: QuestionnaireAnswerMap[] | undefined;
  measureReport: MeasureReport | undefined;
  ownerTask: any;
  requesterTask: any;
  observations: WrappedObservation[] | undefined;
  updateServiceRequest: any;
  refreshServiceRequest: any;
  devMode: boolean;
  showMeasureQuestionnaireResponses: boolean;
  selectedTab: number;
  index: number;
  handleBackdrop: (open: boolean) => void;
}

const TaskDetails = ({
  patient,
  patientDetailsFromReport,
  task,
  serviceRequest,
  planDefinition,
  questionnaire,
  questionnaireResponse,
  questionnaireAnswerMap,
  measureReport,
  ownerTask,
  requesterTask,
  observations,
  updateServiceRequest,
  refreshServiceRequest,
  devMode,
  showMeasureQuestionnaireResponses,
  selectedTab,
  index,
  handleBackdrop,
}: Props) => {
  const user = useAuth().getCurrentUser();
  const { i18n } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const { confirmDialog } = useDialogStore();
  const { componentsData, configurations } = useTenantConfigData();
  const { hjInvitation } = componentsData;

  const [, { create: createBinary }] = useBinarys({ autofetch: false });
  const [, { create: createDocumentReference }] = useDocumentReferences({ autofetch: false });

  const taskWrapper = task && TaskWrapper(task);

  const getMemberDetails =
    patient && memberDetails(patient, patientDetailsFromReport, configurations);
  const getHJAssessment = useMemo(
    () =>
      HJAssessment(
        serviceRequest,
        planDefinition,
        taskWrapper,
        measureReport,
        ownerTask,
        configurations?.fhir,
        i18n
      ),
    [serviceRequest, configurations, i18n]
  );
  const getRequester =
    requesterTask?.resource && task && mapRequester(requesterTask, configurations?.fhir);

  const taskStatus = useMemo(
    () => task && (task?.status === 'requested' || task?.status === 'in-progress'),
    [task]
  );

  const serviceRequestStatus = useMemo(
    () => serviceRequest && serviceRequest?.status === 'active',
    [serviceRequest]
  );

  const natAnswer = () => {
    const taskNumber = task
      ? task?.getInternalNumber?.(configurations?.fhir?.taskInternalNumberIdentifierSystem) ??
        task?.getTaskNumberNAT(configurations?.fhir?.taskNATIdenitifierType) ??
        ''
      : '';
    if (taskNumber === undefined || user?.userName === undefined) return;
    const link = document.createElement('a');
    const url = `${configurations?.mobileAppUrl}/?requestNo=${taskNumber}&request-no=${taskNumber}&id=${user.userName}`;
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
  };

  const resendTaskNotification = async () => {
    if (task?.id === undefined) return;

    const response = await TaskService.resendTask(task?.id);

    enqueueSnackbar(`${response?.status === 'OK' ? 'Message Sent' : 'An error has ocurred'}`, {
      variant: `${response?.status === 'OK' ? 'default' : 'error'}`,
    });
  };

  const closeRequest = async (status?: string) => {
    try {
      const confirms =
        !status &&
        (await confirmDialog({
          confirmText: 'Close it',
          title: 'Close Service Request',
          description: (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Are you sure you want to close this Service Request?
            </Typography>
          ),
        }));

      const setStatus = status ? (status as ServiceRequest['status']) : 'revoked';

      if (confirms || status) {
        await updateServiceRequest(
          produce(serviceRequest!, (draft) => {
            draft.status = setStatus;
          })
        );
        enqueueSnackbar('Request has been updated.');
      }
    } catch (error) {
      enqueueSnackbar('An error has ocurred.', { variant: 'error' });
    }
    refreshServiceRequest();
  };

  const getMeasure = useMemo(
    () => getMeasureReportGroupValue(getMeasureReportGroups(measureReport, configurations?.fhir)),
    [measureReport, configurations]
  );
  const getObservations = useMemo(
    () => getObservationtypeValue(observations, configurations?.fhir),
    [observations, configurations]
  );

  const { page, rowsPerPage } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });
  const getPaginateQuery = () => {
    const patientId = patient?.id || '';
    const paginationQuery: PaginateQuery = { page: page, limit: rowsPerPage };
    paginationQuery.filter = {
      indexedSubjectFhirRefUri: `${configurations?.fhir?.baseUrl}/Patient/${patientId}`,
    };
    return paginationQuery;
  };

  const getCase = async (): Promise<CaseResponse | null> => {
    let existingCase: CaseResponse | null = null;
    const cases: any = (await CaseService.getAll(getPaginateQuery()))?.data;
    cases?.forEach((item) => {
      if (item.workflowScope == 'CASES_WORKFLOW') existingCase = item;
    });
    return existingCase;
  };

  const enroll = async () => {
    let carePlan = await getCase(); // get existing care plan
    try {
      const workflowsByType = await WorkflowService.getWorkflowByType('case');
      const activeWorkflows = Array.isArray(workflowsByType)
        ? workflowsByType.filter((w: any) => w?.status === 'Active')
        : [];
      const workflowScope = activeWorkflows?.[0]?.code;
      if (!workflowScope) {
        enqueueSnackbar(
          'No active case workflow found. Cannot create case with workflow reference.',
          {
            variant: 'error',
          }
        );
        return;
      }
      const payload = {
        resourceType: 'CarePlan',
        intent: 'proposal' as CaseIntent,
        status: 'active' as CaseStatus,
        patientId: patient?.id || '',
        created: new Date().toISOString(),
        workflowScope,
        scope: [
          {
            coding: [
              {
                system: configurations?.fhir?.caseCategorySystem,
                code: configurations?.fhir?.mccCaseScopeCode,
                display: configurations?.fhir?.mccCaseScopeDisplay,
              },
            ],
            text: configurations?.fhir?.mccCaseScopeDisplay,
          },
        ],
        ...(carePlan !== null && { id: carePlan?.fhirId }),
        assignedTo: user?.email,
      };
      const result = await CaseService.create(payload);
      if (!result?.fhirId) {
        enqueueSnackbar('Case creation did not return a valid reference. Please try again.', {
          variant: 'error',
        });
        return;
      }
      enqueueSnackbar('Case Created Succesfully', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('There was an error trying to create case. Please try again', {
        variant: 'error',
      });
    }
  };

  const reprocessMeasureReport = async () => {
    if (measureReport?.id === undefined) return;

    const confirms = await confirmDialog({
      confirmText: 'Agree',
      title: 'Are you sure you want to reprocess the measure report?',
      description: (
        <>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            Current Values
          </Typography>
          {getMeasure?.medicalComplex && (
            <Typography>
              <Item text="Medical complexity" color="success" /> {getMeasure?.medicalComplex}
            </Typography>
          )}
          {getMeasure?.socialComplex && (
            <Typography>
              <Item text="Social complexity" color="info" /> {getMeasure?.socialComplex}
            </Typography>
          )}
          {getMeasure?.serviceIntegration && (
            <Typography>
              <Item text="Service integration levels" color="warning" />{' '}
              {getMeasure?.serviceIntegration}
            </Typography>
          )}

          <br />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            New Values
          </Typography>
          {getObservations?.medicalComplex && (
            <Typography>
              <Item text="Medical complexity" color="success" /> {getObservations?.medicalComplex}
            </Typography>
          )}
          {getObservations?.socialComplex && (
            <Typography>
              <Item text="Social complexity" color="info" /> {getObservations?.socialComplex}
            </Typography>
          )}
          {getObservations?.serviceIntegration && (
            <Typography>
              <Item text="Service integration levels" color="warning" />{' '}
              {getObservations?.serviceIntegration}
            </Typography>
          )}
        </>
      ),
    });

    let response: any;
    if (confirms) {
      response = await TaskService.reprocessMeasureReport(measureReport?.id);
      enqueueSnackbar(`${response?.status === 'OK' ? 'Reprocess' : 'Error Reprocess'}`, {
        variant: `${response?.status === 'OK' ? 'default' : 'error'}`,
      });
    }
  };

  const bodyPrintable = useMemo(
    () =>
      getPrintableTask(
        {
          patient,
          patientDetailsFromReport,
          task,
          serviceRequest,
          planDefinition,
          questionnaire,
          questionnaireAnswerMap,
          questionnaireResponse,
          measureReport,
          ownerTask,
          requesterTask,
        },
        showMeasureQuestionnaireResponses,
        configurations,
        hjInvitation?.showScores
      ),
    [
      patient,
      task,
      serviceRequest,
      planDefinition,
      questionnaire,
      questionnaireResponse,
      measureReport,
      ownerTask,
      requesterTask,
      showMeasureQuestionnaireResponses,
      configurations,
      hjInvitation,
    ]
  );

  const getPrintableReport = async () => {
    handleBackdrop(true);
    try {
      if (taskWrapper?.id !== undefined) {
        const response = await TaskService.getPrintable(bodyPrintable);

        const url = window.URL.createObjectURL(
          new Blob([response?.data, { type: 'application/pdf' }])
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'file.pdf');
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      console.log(error);
    }
    handleBackdrop(false);
  };

  const saveAttachment = async () => {
    handleBackdrop(true);
    try {
      if (taskWrapper?.id !== undefined) {
        const response = await TaskService.getPrintable(bodyPrintable);

        const base64PDF = btoa(String.fromCharCode(...new Uint8Array(response?.data)));

        const PDFPayload = {
          contentType: 'application/pdf',
          data: base64PDF,
          resourceType: 'Binary',
        } as Binary;

        const binary = await createBinary(PDFPayload);
        const dt = new Date();

        if (binary?.length > 0) {
          const mapDocumentReference = {
            content: [
              {
                attachment: {
                  contentType: `${binary?.[0]?.contentType}`,
                  creation: dt.toISOString(),
                  title: `Health_Journey_${taskWrapper?.getTaskNumberNAT(configurations?.fhir?.taskNATIdenitifierType)}_${dt.toISOString().split('T')[0]}`,
                  url: `${configurations?.fhir?.baseUrl}/Binary/${binary?.[0]?.id}`,
                },
              },
            ],
            date: dt.toISOString(),
            masterIdentifier: {
              use: 'official',
              value: 'value document',
            },
            resourceType: 'DocumentReference',
            status: 'current',
            subject: {
              reference: `Patient/${patient?.id}`,
            },
            type: {
              coding: [
                {
                  code: '34133-9',
                  system: 'CodeSystem/4',
                },
              ],
            },
          } as DocumentReference;

          const documentReference = await createDocumentReference(mapDocumentReference);

          if (documentReference?.length > 0) {
            enqueueSnackbar('Attachment has been saved.');
          }
        }
      }
    } catch (error) {
      enqueueSnackbar('An error has ocurred.', { variant: 'error' });
    }
    handleBackdrop(false);
  };

  return (
    <div
      role="tabpanel"
      hidden={selectedTab !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {selectedTab === index && (
        <Stack sx={{ m: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack spacing={1} direction={{ sm: 'row' }} justifyContent="flex-end" sx={{ mb: 3 }}>
                {taskStatus && (
                  <Tooltip title="Resend Notification">
                    <IconButton onClick={() => resendTaskNotification()}>
                      <Iconify icon={'mdi:email-resend'} color="#3dab2b" />
                    </IconButton>
                  </Tooltip>
                )}
                {serviceRequestStatus &&
                  natAnswer &&
                  checkAclValidation({ acls: [crsAcls.CRS.PATIENT.NAT_TASK.ANSWER_TASK] }) && (
                    <Tooltip title="Answer HJ">
                      <IconButton onClick={natAnswer}>
                        <Iconify icon={'ri:survey-fill'} color="#3dab2b" />
                      </IconButton>
                    </Tooltip>
                  )}
                {serviceRequestStatus &&
                  checkAclValidation({ acls: [crsAcls.CRS.PATIENT.NAT_TASK.CANCEL_REQUEST] }) && (
                    <Tooltip title="Cancel Service Request">
                      <IconButton onClick={() => closeRequest()}>
                        <Iconify
                          icon={'octicon:tracked-by-closed-not-planned-16'}
                          color="#ff4842"
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                {serviceRequestStatus &&
                  task &&
                  task?.status === TaskStatusValue.Completed &&
                  checkAclValidation({ acls: [crsAcls.CRS.PATIENT.NAT_TASK.CLOSE_REQUEST] }) && (
                    <Tooltip title="Close Service Request">
                      <IconButton onClick={() => closeRequest('completed')}>
                        <Iconify icon={'octicon:tracked-by-closed-completed-16'} color="#3dab2b" />
                      </IconButton>
                    </Tooltip>
                  )}
                {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.NAT_TASK.PRINT_TASK] }) && (
                  <Tooltip title="Print">
                    <IconButton onClick={() => getPrintableReport()}>
                      <Iconify icon={'material-symbols:print'} color="#3dab2b" />
                    </IconButton>
                  </Tooltip>
                )}
                {task && task?.status === TaskStatusValue.Completed && (
                  <Tooltip title="Reprocess Measure">
                    <IconButton onClick={() => reprocessMeasureReport()}>
                      <Iconify icon={'material-symbols:cached'} color="#3dab2b" />
                    </IconButton>
                  </Tooltip>
                )}
                {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.NAT_TASK.ENROLL] }) &&
                  getMeasure?.serviceIntegration && (
                    <Tooltip title="Enroll to care coordination">
                      <IconButton onClick={() => enroll()}>
                        <Iconify icon={'material-symbols:signature'} color="#3dab2b" />
                      </IconButton>
                    </Tooltip>
                  )}
                <Tooltip title="Save as Attachment">
                  <IconButton onClick={() => saveAttachment()}>
                    <Iconify icon={'material-symbols:file-save-rounded'} color="#3dab2b" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} lg={4}>
              <DetailsSummary
                children={
                  <Link
                    to={PATH_DASHBOARD.crs.patient.details.forId(patient?.id ?? '')}
                    target="_blank"
                  >
                    {i18n('nat.patient.title', 'translations')}
                  </Link>
                }
                data={getMemberDetails}
                devMode={devMode}
              />
            </Grid>

            <Grid item xs={12} md={4} lg={4}>
              <DetailsSummary
                title={i18n('nat.request.title', 'translations')}
                data={getHJAssessment}
                devMode={devMode}
              />
            </Grid>

            <Grid item xs={12} md={4} lg={4}>
              <DetailsSummary title="Requester" data={getRequester} />
            </Grid>

            {hjInvitation && (
              <Grid item xs={12}>
                <RequestReport
                  questionnaire={questionnaire}
                  questionnaireResponse={questionnaireResponse}
                  questionnaireAnswerMap={questionnaireAnswerMap}
                  measureReport={measureReport}
                  devMode={devMode}
                  showMeasureQuestionnaireResponses={showMeasureQuestionnaireResponses}
                  configurations={configurations?.fhir}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <RequestReportResponse
                questionnaire={questionnaire}
                questionnaireResponse={questionnaireResponse}
                questionnaireAnswerMap={questionnaireAnswerMap}
                devMode={devMode}
              />
            </Grid>
          </Grid>
        </Stack>
      )}
    </div>
  );
};

export default TaskDetails;
